import type { Vec3 } from '../../geo/types';
import type { RoofProfile } from './roofProfile';

/**
 * LOD1 details; a door is two, so its leaf and its transom light each keep their height; a wall anchor
 * is one, and so is a basket of loaves on a shop's counter.
 */
export type DetailKind =
  'window' | 'doorLeaf' | 'doorTransom' | 'loftDoor' | 'smallWindow' | 'shop' | 'anchor' | 'loaves';

export const DETAIL_KINDS: readonly DetailKind[] = [
  'window',
  'doorLeaf',
  'doorTransom',
  'loftDoor',
  'smallWindow',
  'shop',
  'anchor',
  'loaves',
];

/** Where an opening sits in the world: its bottom-left corner on the wall face and its size. */
export interface DetailPose {
  readonly origin: Vec3;
  /** Vector for one local metre along the wall, to the right seen from outside. */
  readonly right: Vec3;
  /** Vector for one local metre out of the wall; oblique frames also transform details. */
  readonly outward: Vec3;
  readonly width: number;
  readonly height: number;
}

export interface DetailSink {
  add(kind: DetailKind, pose: DetailPose, edgeId: number): void;
  roof?(profile: RoofProfile): void;
}

/** Every opening of one kind, as instance matrices of a template one unit wide and one tall. */
export interface DetailInstances {
  readonly kind: DetailKind;
  readonly count: number;
  /** Column-major 4x4 matrices, 16 floats each: x across the opening, y up it, z out in metres. */
  readonly matrices: Float32Array;
  readonly edgeIds: Float32Array;
}

export interface DetailCollector extends DetailSink {
  readonly roofs: readonly RoofProfile[];
  roof(profile: RoofProfile): void;
  finish(): readonly DetailInstances[];
}

interface Growing {
  matrices: number[];
  edgeIds: number[];
}

function writePose(out: number[], pose: DetailPose): void {
  const { origin: o, right: r, outward: n, width: w, height: h } = pose;
  out.push(r[0] * w, r[1] * w, r[2] * w, 0, 0, h, 0, 0, n[0], n[1], n[2], 0, o[0], o[1], o[2], 1);
}

export function createDetailCollector(): DetailCollector {
  const roofs: RoofProfile[] = [];
  const byKind = new Map<DetailKind, Growing>(
    DETAIL_KINDS.map((k) => [k, { matrices: [], edgeIds: [] }])
  );
  return {
    roofs,
    roof(profile) {
      roofs.push(profile);
    },
    add(kind, pose, edgeId) {
      const target = byKind.get(kind)!;
      writePose(target.matrices, pose);
      target.edgeIds.push(edgeId);
    },
    finish() {
      return DETAIL_KINDS.map((kind) => {
        const { matrices, edgeIds } = byKind.get(kind)!;
        return {
          kind,
          count: edgeIds.length,
          matrices: Float32Array.from(matrices),
          edgeIds: Float32Array.from(edgeIds),
        };
      });
    },
  };
}
